import { CheckCircle2, FileUp, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { assignBookmarkToProject, createBookmark } from '@/api/bookmarks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { parseBookmarksHtml, type RawBookmark } from '@/lib/parse-bookmarks-html';

type Phase =
  | { kind: 'idle' }
  | { kind: 'parsed'; importable: RawBookmark[]; skipped: RawBookmark[] }
  | { kind: 'importing'; total: number; done: number }
  | {
      kind: 'done';
      succeeded: number;
      failed: { url: string; title: string; error: string }[];
    };

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  onImported: () => void;
}

export function ImportBookmarksModal({ open, onClose, projectId, projectName, onImported }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [folder, setFolder] = useState('');
  const [tag, setTag] = useState('imported');
  const [parseError, setParseError] = useState<string | null>(null);

  function reset() {
    setPhase({ kind: 'idle' });
    setFolder('');
    setTag('imported');
    setParseError(null);
  }

  async function handleFile(file: File) {
    setParseError(null);
    try {
      const text = await file.text();
      const result = parseBookmarksHtml(text, folder);
      if (folder.trim() && !result.folderFound) {
        setParseError(`No folder named "${folder.trim()}" was found in the file.`);
        return;
      }
      if (result.importable.length === 0 && result.skipped.length === 0) {
        setParseError('No bookmarks found in the file.');
        return;
      }
      setPhase({ kind: 'parsed', importable: result.importable, skipped: result.skipped });
    } catch (err) {
      setParseError(`Could not parse the file: ${(err as Error).message}`);
    }
  }

  async function handleImport() {
    if (phase.kind !== 'parsed') return;
    const tags = tag.trim()
      ? tag
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter((t) => t.length > 0)
      : [];
    const items = phase.importable;
    setPhase({ kind: 'importing', total: items.length, done: 0 });

    const failed: { url: string; title: string; error: string }[] = [];
    let succeeded = 0;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!item) continue;
      try {
        const created = await createBookmark({
          url: item.url,
          title: item.title,
          tags,
        });
        await assignBookmarkToProject(created.id, projectId);
        succeeded += 1;
      } catch (err) {
        failed.push({ url: item.url, title: item.title, error: (err as Error).message });
      }
      setPhase({ kind: 'importing', total: items.length, done: i + 1 });
    }

    setPhase({ kind: 'done', succeeded, failed });
    if (succeeded > 0) onImported();
    if (failed.length === 0) {
      toast.success(`Imported ${succeeded} bookmarks into "${projectName}"`);
    } else {
      toast.error(`${succeeded} imported, ${failed.length} failed`);
    }
  }

  function handleClose() {
    if (phase.kind === 'importing') return;
    reset();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : handleClose())}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import bookmarks into "{projectName}"</DialogTitle>
          <DialogDescription>
            Upload a Chrome / Firefox / Edge HTML bookmark export. Each bookmark will be created and
            assigned to this project.
          </DialogDescription>
        </DialogHeader>

        {phase.kind === 'idle' && (
          <IdlePhase
            folder={folder}
            setFolder={setFolder}
            tag={tag}
            setTag={setTag}
            error={parseError}
            onFile={handleFile}
          />
        )}

        {phase.kind === 'parsed' && (
          <ParsedPhase importable={phase.importable} skipped={phase.skipped} folder={folder} />
        )}

        {phase.kind === 'importing' && <ImportingPhase done={phase.done} total={phase.total} />}

        {phase.kind === 'done' && <DonePhase succeeded={phase.succeeded} failed={phase.failed} />}

        <DialogFooter>
          {phase.kind === 'idle' && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
          {phase.kind === 'parsed' && (
            <>
              <Button variant="outline" onClick={() => setPhase({ kind: 'idle' })}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={phase.importable.length === 0}>
                Import {phase.importable.length} bookmarks
              </Button>
            </>
          )}
          {phase.kind === 'importing' && (
            <Button variant="outline" disabled>
              Importing…
            </Button>
          )}
          {phase.kind === 'done' && <Button onClick={handleClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IdlePhase({
  folder,
  setFolder,
  tag,
  setTag,
  error,
  onFile,
}: {
  folder: string;
  setFolder: (v: string) => void;
  tag: string;
  setTag: (v: string) => void;
  error: string | null;
  onFile: (f: File) => void | Promise<void>;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-muted-foreground bg-zinc-900/60 border border-white/5 rounded-md p-3 leading-relaxed">
        <p className="mb-2">
          <span className="text-foreground font-medium">In Chrome / Edge:</span> right-click the tab
          strip → <em>"Bookmark all tabs…"</em> → save into a new folder.
        </p>
        <p>
          Then open the bookmark manager (<code>Ctrl+Shift+O</code>) → ⋮ menu →{' '}
          <em>"Export bookmarks"</em>. Pick the saved <code>.html</code> file below.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="import-file">Bookmarks file (.html)</Label>
        <input
          id="import-file"
          type="file"
          accept=".html,.htm,text/html"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
          className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer file:text-sm file:font-medium"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="import-folder">
          Folder name (optional) — only import bookmarks from this folder
        </Label>
        <Input
          id="import-folder"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          placeholder="e.g. Open Tabs - 2026-05-03"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="import-tag">Tag(s) to apply (comma-separated)</Label>
        <Input
          id="import-tag"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          placeholder="imported"
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function ParsedPhase({
  importable,
  skipped,
  folder,
}: {
  importable: RawBookmark[];
  skipped: RawBookmark[];
  folder: string;
}) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-foreground">
        Found <span className="font-semibold">{importable.length}</span> importable bookmarks
        {folder.trim() ? <> under folder "{folder.trim()}"</> : <> in the file</>}.
      </p>
      {skipped.length > 0 && (
        <p className="text-muted-foreground">
          Skipping {skipped.length} URL{skipped.length === 1 ? '' : 's'} with browser-internal
          schemes (chrome://, about:, etc.).
        </p>
      )}
      {importable.length > 0 && (
        <div className="max-h-64 overflow-auto rounded-md border border-white/5 bg-zinc-900/60 p-2 text-xs">
          {importable.slice(0, 50).map((b) => (
            <div key={b.url} className="truncate py-0.5">
              <span className="text-foreground">{b.title}</span>
              <span className="text-muted-foreground"> · {b.url}</span>
            </div>
          ))}
          {importable.length > 50 && (
            <div className="py-0.5 text-muted-foreground">… and {importable.length - 50} more</div>
          )}
        </div>
      )}
    </div>
  );
}

function ImportingPhase({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p>
        Importing… {done} / {total}
      </p>
      <div className="h-2 w-full rounded-full bg-zinc-900/60 overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DonePhase({
  succeeded,
  failed,
}: {
  succeeded: number;
  failed: { url: string; title: string; error: string }[];
}) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        <span>
          <span className="font-semibold">{succeeded}</span> bookmark
          {succeeded === 1 ? '' : 's'} imported.
        </span>
      </p>
      {failed.length > 0 && (
        <>
          <p className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-destructive" />
            <span>
              <span className="font-semibold">{failed.length}</span> failed:
            </span>
          </p>
          <div className="max-h-64 overflow-auto rounded-md border border-white/5 bg-zinc-900/60 p-2 text-xs">
            {failed.map((f) => (
              <div key={f.url} className="py-0.5">
                <div className="truncate text-foreground">{f.title}</div>
                <div className="truncate text-muted-foreground">{f.url}</div>
                <div className="truncate text-destructive">{f.error}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Re-export for convenience.
export { FileUp };
